import React, { useEffect } from "react";
import { MapPin, Utensils, GlassWater, Landmark } from "lucide-react";
import { Card, Input, PillGroup, RepeatableGroup, Select, PlaceSearch } from "./FormComponents";
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const MapViewUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
            if (center[0] && center[1]) {
                map.flyTo(center, 17, { duration: 1.5 });
            }
        }, 100);
    }, [center, map]);
    return null;
};

// Haversine distance in meters
const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const getWalkingTime = (meters) => {
    const minutes = Math.round(meters / 80);
    if (minutes < 1) return 'Less than 1 min walk';
    if (minutes === 1) return '1 min walk';
    return `${minutes} min walk`;
};

export const Step14 = ({ data, updateData, isLight, showValidation, propertyLocation }) => {
  const handleChange = (field, value) => {
    updateData("localGuide", { ...data, [field]: value });
  };

  const handleItemUpdate = (collection, index, field, value) => {
    const newItems = [...data[collection]];
    newItems[index] = { ...newItems[index], [field]: value };
    handleChange(collection, newItems);
  };

  const handlePlaceSelect = (collection, index, place) => {
    const newItems = [...data[collection]];
    
    // Attempt to map Google types to our categories
    let category = newItems[index].category;
    let type = newItems[index].type;

    if (collection === "essentialServices") {
      const typeMap = {
        'supermarket': 'Supermarket',
        'convenience_store': 'Convenience Store',
        'pharmacy': 'Pharmacy',
        'hospital': 'Hospital/Clinic',
        'atm': 'ATM/Bank',
        'post_office': 'Post Office',
        'laundry': 'Laundry',
        'bakery': 'Bakery'
      };
      const foundType = place.types?.find(t => typeMap[t]);
      if (foundType) type = typeMap[foundType];
    } else if (collection === "restaurants") {
      const typeMap = {
        'restaurant': 'International',
        'cafe': 'Café',
        'meal_takeaway': 'Souvlaki/Fast Food'
      };
      const foundType = place.types?.find(t => typeMap[t]);
      if (foundType) category = typeMap[foundType];
    } else if (collection === "bars") {
      const typeMap = {
        'bar': 'Cocktail Bar',
        'night_club': 'Club'
      };
      const foundType = place.types?.find(t => typeMap[t]);
      if (foundType) type = typeMap[foundType];
    } else if (collection === "attractions") {
      const typeMap = {
        'museum': 'Museum',
        'park': 'Park',
        'tourist_attraction': 'Other',
        'place_of_worship': 'Religious Site',
        'natural_feature': 'Park',
        'shopping_mall': 'Shopping'
      };
      const foundType = place.types?.find(t => typeMap[t]);
      if (foundType) type = typeMap[foundType];
    }

    let distance = newItems[index].distance;
    if (propertyLocation?.lat && propertyLocation?.lon && place.lat && place.lon) {
      const meters = haversineDistance(propertyLocation.lat, propertyLocation.lon, place.lat, place.lon);
      distance = getWalkingTime(meters);
    }

    newItems[index] = { 
      ...newItems[index], 
      name: place.name, 
      address: place.address,
      lat: place.lat,
      lon: place.lon,
      hours: place.hours || "",
      distance,
      category,
      type
    };

    handleChange(collection, newItems);
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Essential Services" icon={MapPin} subtitle="Useful nearby services guests may need during their stay." isLight={isLight}>
        <RepeatableGroup 
          label="Services" items={data.essentialServices} isLight={isLight}
          onAdd={() => handleChange("essentialServices", [...data.essentialServices, { id: Date.now().toString(), type: "", name: "", address: "", hours: "", distance: "", lat: null, lon: null, notes: "" }])}
          onRemove={(index) => handleChange("essentialServices", data.essentialServices.filter((_, i) => i !== index))}
          renderItem={(item, index) => (
            <div className="flex flex-col gap-6">
              <PlaceSearch 
                label="Search Service" isLight={isLight} value={item.name}
                locationBias={propertyLocation}
                onSelect={(place) => handlePlaceSelect("essentialServices", index, place)}
              />
              
              <div className={`h-64 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                <MapContainer 
                  center={[item.lat || 37.9838, item.lon || 23.7275]} 
                  zoom={item.lat ? 17 : 13} 
                  scrollWheelZoom={true} 
                  style={{ height: '100%', width: '100%' }}
                  className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                >
                  {isLight ? (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  ) : (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  )}
                  {item.lat && item.lon && <Marker position={[item.lat, item.lon]} />}
                  <MapViewUpdater center={[item.lat || 37.9838, item.lon || 23.7275]} />
                </MapContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select 
                  label="Type" required options={["Supermarket", "Convenience Store", "Pharmacy", "Hospital/Clinic", "ATM/Bank", "Post Office", "Laundry", "Bakery", "Other"]}
                  value={item.type} isLight={isLight} showValidation={showValidation}
                  onChange={(e) => handleItemUpdate("essentialServices", index, "type", e.target.value)}
                />
                <Input label="Name" required value={item.name} isLight={isLight} showValidation={showValidation} onChange={(e) => handleItemUpdate("essentialServices", index, "name", e.target.value)} />
                <Input label="Address" required value={item.address} isLight={isLight} showValidation={showValidation} onChange={(e) => handleItemUpdate("essentialServices", index, "address", e.target.value)} />
                <Input label="Business Hours" placeholder="Mon-Sun: 08:00 - 20:00" value={item.hours} isLight={isLight} onChange={(e) => handleItemUpdate("essentialServices", index, "hours", e.target.value)} />
                <div className="flex flex-col gap-1">
                  <Input label="Walking Distance" placeholder="2 min walk" value={item.distance} isLight={isLight} onChange={(e) => handleItemUpdate("essentialServices", index, "distance", e.target.value)} />
                  {item.distance && (
                    <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      Auto-calculated from property location
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Input label="Notes" value={item.notes} isLight={isLight} onChange={(e) => handleItemUpdate("essentialServices", index, "notes", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        />
      </Card>

      <Card title="Restaurants & Cafés" icon={Utensils} subtitle="Recommended places to eat or have coffee nearby." isLight={isLight}>
        <RepeatableGroup 
          label="Venues" items={data.restaurants} isLight={isLight}
          onAdd={() => handleChange("restaurants", [...data.restaurants, { id: Date.now().toString(), name: "", category: "", address: "", hours: "", distance: "", lat: null, lon: null, whyGo: "" }])}
          onRemove={(index) => handleChange("restaurants", data.restaurants.filter((_, i) => i !== index))}
          renderItem={(item, index) => (
            <div className="flex flex-col gap-6">
              <PlaceSearch 
                label="Search Venue" isLight={isLight} value={item.name}
                locationBias={propertyLocation}
                onSelect={(place) => handlePlaceSelect("restaurants", index, place)}
              />

              <div className={`h-64 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                <MapContainer 
                  center={[item.lat || 37.9838, item.lon || 23.7275]} 
                  zoom={item.lat ? 17 : 13} 
                  scrollWheelZoom={true} 
                  style={{ height: '100%', width: '100%' }}
                  className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                >
                  {isLight ? (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  ) : (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  )}
                  {item.lat && item.lon && <Marker position={[item.lat, item.lon]} />}
                  <MapViewUpdater center={[item.lat || 37.9838, item.lon || 23.7275]} />
                </MapContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Name" required value={item.name} isLight={isLight} showValidation={showValidation} onChange={(e) => handleItemUpdate("restaurants", index, "name", e.target.value)} />
                <Select 
                  label="Category" required options={["Greek/Traditional", "Mediterranean", "International", "Souvlaki/Fast Food", "Seafood", "Vegetarian/Vegan", "Café", "Brunch", "Rooftop", "Other"]}
                  value={item.category} isLight={isLight} showValidation={showValidation}
                  onChange={(e) => handleItemUpdate("restaurants", index, "category", e.target.value)}
                />
                <Input label="Address" value={item.address} isLight={isLight} onChange={(e) => handleItemUpdate("restaurants", index, "address", e.target.value)} />
                <Input label="Business Hours" placeholder="Mon-Sun: 08:00 - 20:00" value={item.hours} isLight={isLight} onChange={(e) => handleItemUpdate("restaurants", index, "hours", e.target.value)} />
                <div className="flex flex-col gap-1">
                  <Input label="Walking Distance" value={item.distance} isLight={isLight} onChange={(e) => handleItemUpdate("restaurants", index, "distance", e.target.value)} />
                  {item.distance && (
                    <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      Auto-calculated from property location
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Input label="Why go here?" placeholder="Best souvlaki in the area." value={item.whyGo} isLight={isLight} onChange={(e) => handleItemUpdate("restaurants", index, "whyGo", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        />
      </Card>

      <Card title="Bars & Nightlife" icon={GlassWater} subtitle="Nearby places for drinks, nightlife, and evening outings." isLight={isLight}>
        <RepeatableGroup 
          label="Venues" items={data.bars} isLight={isLight}
          onAdd={() => handleChange("bars", [...data.bars, { id: Date.now().toString(), name: "", type: "", address: "", hours: "", distance: "", lat: null, lon: null, whyGo: "" }])}
          onRemove={(index) => handleChange("bars", data.bars.filter((_, i) => i !== index))}
          renderItem={(item, index) => (
            <div className="flex flex-col gap-6">
              <PlaceSearch 
                label="Search Venue" isLight={isLight} value={item.name}
                locationBias={propertyLocation}
                onSelect={(place) => handlePlaceSelect("bars", index, place)}
              />

              <div className={`h-64 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                <MapContainer 
                  center={[item.lat || 37.9838, item.lon || 23.7275]} 
                  zoom={item.lat ? 17 : 13} 
                  scrollWheelZoom={true} 
                  style={{ height: '100%', width: '100%' }}
                  className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                >
                  {isLight ? (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  ) : (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  )}
                  {item.lat && item.lon && <Marker position={[item.lat, item.lon]} />}
                  <MapViewUpdater center={[item.lat || 37.9838, item.lon || 23.7275]} />
                </MapContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Name" required value={item.name} isLight={isLight} showValidation={showValidation} onChange={(e) => handleItemUpdate("bars", index, "name", e.target.value)} />
                <Select 
                  label="Type" required options={["Cocktail Bar", "Wine Bar", "Beer Bar", "Rooftop Bar", "Club", "Live Music", "Other"]}
                  value={item.type} isLight={isLight} showValidation={showValidation}
                  onChange={(e) => handleItemUpdate("bars", index, "type", e.target.value)}
                />
                <Input label="Address" value={item.address} isLight={isLight} onChange={(e) => handleItemUpdate("bars", index, "address", e.target.value)} />
                <Input label="Business Hours" placeholder="Mon-Sun: 08:00 - 20:00" value={item.hours} isLight={isLight} onChange={(e) => handleItemUpdate("bars", index, "hours", e.target.value)} />
                <div className="flex flex-col gap-1">
                  <Input label="Walking Distance" value={item.distance} isLight={isLight} onChange={(e) => handleItemUpdate("bars", index, "distance", e.target.value)} />
                  {item.distance && (
                    <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      Auto-calculated from property location
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Input label="Why go here?" value={item.whyGo} isLight={isLight} onChange={(e) => handleItemUpdate("bars", index, "whyGo", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        />
      </Card>

      <Card title="Attractions & Landmarks" icon={Landmark} subtitle="Notable places and points of interest guests can visit." isLight={isLight}>
        <RepeatableGroup 
          label="Sites" items={data.attractions} isLight={isLight}
          onAdd={() => handleChange("attractions", [...data.attractions, { id: Date.now().toString(), name: "", type: "", address: "", hours: "", distance: "", lat: null, lon: null, whyVisit: "", insiderTip: "" }])}
          onRemove={(index) => handleChange("attractions", data.attractions.filter((_, i) => i !== index))}
          renderItem={(item, index) => (
            <div className="flex flex-col gap-6">
              <PlaceSearch 
                label="Search Attraction" isLight={isLight} value={item.name}
                locationBias={propertyLocation}
                onSelect={(place) => handlePlaceSelect("attractions", index, place)}
              />

              <div className={`h-64 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                <MapContainer 
                  center={[item.lat || 37.9838, item.lon || 23.7275]} 
                  zoom={item.lat ? 17 : 13} 
                  scrollWheelZoom={true} 
                  style={{ height: '100%', width: '100%' }}
                  className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                >
                  {isLight ? (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  ) : (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  )}
                  {item.lat && item.lon && <Marker position={[item.lat, item.lon]} />}
                  <MapViewUpdater center={[item.lat || 37.9838, item.lon || 23.7275]} />
                </MapContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Name" required value={item.name} isLight={isLight} showValidation={showValidation} onChange={(e) => handleItemUpdate("attractions", index, "name", e.target.value)} />
                <Select 
                  label="Type" required options={["Archaeological Site", "Museum", "Park", "Beach", "Market", "Religious Site", "Viewpoint", "Shopping", "Other"]}
                  value={item.type} isLight={isLight} showValidation={showValidation}
                  onChange={(e) => handleItemUpdate("attractions", index, "type", e.target.value)}
                />
                <Input label="Address" value={item.address} isLight={isLight} onChange={(e) => handleItemUpdate("attractions", index, "address", e.target.value)} />
                <Input label="Business Hours" placeholder="Mon-Sun: 08:00 - 20:00" value={item.hours} isLight={isLight} onChange={(e) => handleItemUpdate("attractions", index, "hours", e.target.value)} />
                <div className="flex flex-col gap-1">
                  <Input label="Walking Distance" value={item.distance} isLight={isLight} onChange={(e) => handleItemUpdate("attractions", index, "distance", e.target.value)} />
                  {item.distance && (
                    <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      Auto-calculated from property location
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Input label="Why visit?" value={item.whyVisit} isLight={isLight} onChange={(e) => handleItemUpdate("attractions", index, "whyVisit", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Input label="Insider tip" placeholder="Free entry first Sunday of the month." value={item.insiderTip} isLight={isLight} onChange={(e) => handleItemUpdate("attractions", index, "insiderTip", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        />
      </Card>
    </div>
  );
};
